/**
 * Inventory Adjustments, Batches, and Stock Movement queries — PRD.191.
 *
 * The stock_movements ledger is the single source of truth for current
 * stock and batch availability. Item-level current stock is derived from
 * SUM(quantity_in) - SUM(quantity_out) filtered by inventory_item_id.
 * Batch-level available quantity uses the same sum filtered by
 * inventory_batch_id.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface InventoryBatchRow {
  id: string;
  org_id: string | null;
  inventory_item_id: string | null;
  batch_number: string | null;
  expiry_date: string | null;
  received_date: string | null;
  status: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InventoryAdjustmentRow {
  id: string;
  org_id: string | null;
  adjustment_number: string | null;
  adjustment_date: string | null;
  location: string | null;
  remarks: string | null;
  status: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InventoryAdjustmentLineRow {
  id: string;
  org_id: string | null;
  inventory_adjustment_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  movement_direction: string | null; // 'add' | 'deduct'
  quantity: number | null;
  unit: string | null;
  reason: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface StockMovementRow {
  id: string;
  org_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  source_type: string | null;
  source_id: string | null;
  source_line_id: string | null;
  movement_type: string | null;
  quantity_in: number | null;
  quantity_out: number | null;
  unit: string | null;
  movement_date: string | null;
  remarks: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Aggregate row shapes (returned by ledger summary queries) ───

export interface InventoryItemStockRow {
  inventory_item_id: string;
  current_stock: number;
}

export interface InventoryBatchAvailabilityRow {
  inventory_batch_id: string;
  available_quantity: number;
}

// ─── Adjustment list / detail queries ───

export function qInventoryAdjustmentsList(): QuerySpec<[]> {
  return {
    key: ['inventory-adjustments-list'] as const,
    sql: `
      SELECT * FROM inventory_adjustments
      ORDER BY adjustment_date DESC, created_at DESC
    `,
    params: [],
    watch: ['inventory_adjustments'],
  };
}

export function qInventoryAdjustmentLinesByAdjustment(adjustmentId: string): QuerySpec<[string]> {
  return {
    key: ['inventory-adjustment-lines', adjustmentId] as const,
    sql: `
      SELECT * FROM inventory_adjustment_lines
      WHERE inventory_adjustment_id = ?
      ORDER BY created_at ASC
    `,
    params: [adjustmentId],
    watch: ['inventory_adjustment_lines'],
  };
}

// ─── Batch queries ───

export function qInventoryBatchesByItem(itemId: string): QuerySpec<[string]> {
  return {
    key: ['inventory-batches-by-item', itemId] as const,
    sql: `
      SELECT * FROM inventory_batches
      WHERE inventory_item_id = ?
      ORDER BY
        CASE WHEN expiry_date IS NULL THEN 1 ELSE 0 END,
        expiry_date ASC,
        batch_number ASC
    `,
    params: [itemId],
    watch: ['inventory_batches'],
  };
}

/**
 * Available quantity per batch for a given inventory item, derived from
 * stock_movements. Returns one row per batch — including batches whose
 * net quantity is zero (so the picker can display them as depleted).
 */
export function qInventoryBatchAvailableQuantities(itemId: string): QuerySpec<[string]> {
  return {
    key: ['inventory-batch-availability', itemId] as const,
    sql: `
      SELECT
        sm.inventory_batch_id,
        COALESCE(SUM(COALESCE(sm.quantity_in, 0)), 0)
          - COALESCE(SUM(COALESCE(sm.quantity_out, 0)), 0) AS available_quantity
      FROM stock_movements sm
      WHERE sm.inventory_item_id = ?
        AND sm.inventory_batch_id IS NOT NULL
      GROUP BY sm.inventory_batch_id
    `,
    params: [itemId],
    watch: ['stock_movements'],
  };
}

// ─── Item-level current stock ───

/**
 * Current stock for a single inventory item, derived from the ledger.
 * Returns one row with current_stock = 0 when there are no movements yet.
 */
export function qInventoryItemCurrentStock(itemId: string): QuerySpec<[string]> {
  return {
    key: ['inventory-item-current-stock', itemId] as const,
    sql: `
      SELECT
        ? AS inventory_item_id,
        COALESCE(SUM(COALESCE(quantity_in, 0)), 0)
          - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS current_stock
      FROM stock_movements
      WHERE inventory_item_id = ?
    `,
    params: [itemId],
    watch: ['stock_movements'],
  };
}

/**
 * Current stock for every inventory item that has at least one movement.
 * Use together with the item list — items with zero movements simply
 * won't appear in this result and the consumer should fall back to
 * `inventory_items.initial_stock_quantity` for backward compatibility
 * (see PRD.191 §9).
 */
export function qInventoryItemsCurrentStock(): QuerySpec<[]> {
  return {
    key: ['inventory-items-current-stock'] as const,
    sql: `
      SELECT
        inventory_item_id,
        COALESCE(SUM(COALESCE(quantity_in, 0)), 0)
          - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS current_stock
      FROM stock_movements
      GROUP BY inventory_item_id
    `,
    params: [],
    watch: ['stock_movements'],
  };
}

// ─── Stock movement audit query ───

export function qStockMovementsByItem(itemId: string): QuerySpec<[string]> {
  return {
    key: ['stock-movements-by-item', itemId] as const,
    sql: `
      SELECT * FROM stock_movements
      WHERE inventory_item_id = ?
      ORDER BY movement_date DESC, created_at DESC
    `,
    params: [itemId],
    watch: ['stock_movements'],
  };
}
